import katex from "katex";
import "katex/dist/katex.min.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MdBolt,
  MdCheck,
  MdCheckCircle,
  MdClose,
  MdCode,
  MdContentCut,
  MdEdit,
  MdExpandMore,
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdFormatUnderlined,
  MdHourglassEmpty,
  MdLightbulb,
  MdPushPin,
  MdRefresh,
  MdSend,
} from "react-icons/md";
import {
  createEditor,
  Editor,
  Element as SlateElement,
  Transforms,
} from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";
import { editSelection, postProcessText } from "../../../services/api";
import ExportButton from "../../common/ExportButton";
import ExportModal from "../../common/ExportModal";
import styles from "./RichTextEditor.module.css";

// Render LaTeX to HTML string, returning raw LaTeX on error
const renderLatex = (latex, displayMode = false) => {
  try {
    return katex.renderToString(latex.trim(), {
      displayMode,
      throwOnError: false,
      output: "html",
    });
  } catch {
    return displayMode ? `\\[${latex}\\]` : `\\(${latex}\\)`;
  }
};

const RichTextEditor = ({
  content,
  onUpdate,
  isStreaming,
  citations = [],
  onCitationClick,
  onSave,
  exportData = {},
  isLoading,
  hasSummary = true,
}) => {
  const [showPreview, setShowPreview] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const [lineCount, setLineCount] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    original: "",
    processed: "",
    operation: "",
  });
  const isInternalUpdate = useRef(false);
  const containerRef = useRef(null);
  const [processingPos, setProcessingPos] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Track sentences that have been edited (their citations should be invalidated)
  const [editedSentences, setEditedSentences] = useState(new Set());
  const [isSaved, setIsSaved] = useState(false);

  const handleSaveClick = () => {
    if (onSave) {
      onSave();
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1000);
    }
  };

  const isBusy = isLoading || isStreaming;

  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor()));
    const { isVoid } = e;
    e.isVoid = (element) =>
      element.type === "math-block" ? true : isVoid(element);
    return e;
  }, []);

  // Convert markdown string to Slate value
  const markdownToSlate = useCallback((markdown) => {
    if (!markdown) return [{ type: "paragraph", children: [{ text: "" }] }];

    const lines = markdown.split("\n");
    const nodes = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Skip blank lines
      if (line.trim() === "") {
        i++;
        continue;
      }

      // Display math block: \[ ... \] (may span multiple lines)
      if (line.trim() === "\\[") {
        const mathLines = [];
        i++;
        while (i < lines.length && lines[i].trim() !== "\\]") {
          mathLines.push(lines[i]);
          i++;
        }
        i++; // skip closing \]
        nodes.push({
          type: "math-block",
          latex: mathLines.join("\n"),
          children: [{ text: "" }],
        });
        continue;
      }

      // Display math on single line: \[ ... \]
      const singleDisplayMatch = line.match(/^\\\[(.+?)\\\]$/);
      if (singleDisplayMatch) {
        nodes.push({
          type: "math-block",
          latex: singleDisplayMatch[1],
          children: [{ text: "" }],
        });
        i++;
        continue;
      }

      // $$ ... $$ display math block (multi-line)
      if (line.trim() === "$$") {
        const mathLines = [];
        i++;
        while (i < lines.length && lines[i].trim() !== "$$") {
          mathLines.push(lines[i]);
          i++;
        }
        i++; // skip closing $$
        nodes.push({
          type: "math-block",
          latex: mathLines.join("\n"),
          children: [{ text: "" }],
        });
        continue;
      }

      // Headers
      if (line.startsWith("### ")) {
        nodes.push({
          type: "heading-three",
          children: [{ text: line.slice(4) }],
        });
        i++;
        continue;
      }
      if (line.startsWith("## ")) {
        nodes.push({
          type: "heading-two",
          children: [{ text: line.slice(3) }],
        });
        i++;
        continue;
      }
      if (line.startsWith("# ")) {
        nodes.push({
          type: "heading-one",
          children: [{ text: line.slice(2) }],
        });
        i++;
        continue;
      }
      // Lists
      if (line.match(/^\d+\.\s/)) {
        nodes.push({
          type: "numbered-list-item",
          children: [{ text: line.replace(/^\d+\.\s/, "") }],
        });
        i++;
        continue;
      }
      if (line.match(/^[-*]\s/)) {
        nodes.push({
          type: "bulleted-list-item",
          children: [{ text: line.replace(/^[-*]\s/, "") }],
        });
        i++;
        continue;
      }
      // Blockquote
      if (line.startsWith("> ")) {
        nodes.push({
          type: "block-quote",
          children: [{ text: line.slice(2) }],
        });
        i++;
        continue;
      }
      // Code block
      if (line.startsWith("```")) {
        nodes.push({ type: "code-block", children: [{ text: "" }] });
        i++;
        continue;
      }
      // Default paragraph with inline formatting
      const children = parseInlineFormatting(line);
      nodes.push({ type: "paragraph", children });
      i++;
    }

    return nodes.length > 0
      ? nodes
      : [{ type: "paragraph", children: [{ text: "" }] }];
  }, []);

  // Parse inline markdown formatting
  const parseInlineFormatting = (text) => {
    if (!text) return [{ text: "" }];

    // Simple inline parsing - just return as plain text for now
    // Full markdown parsing would require a more complex implementation
    return [{ text }];
  };

  // Convert Slate value to markdown string
  const slateToMarkdown = useCallback((nodes) => {
    return nodes
      .map((node) => {
        const text = node.children
          .map((child) => {
            let str = child.text || "";
            if (child.bold) str = `**${str}**`;
            if (child.italic) str = `*${str}*`;
            if (child.underline) str = `_${str}_`;
            if (child.code) str = `\`${str}\``;
            return str;
          })
          .join("");

        switch (node.type) {
          case "heading-one":
            return `# ${text}`;
          case "heading-two":
            return `## ${text}`;
          case "heading-three":
            return `### ${text}`;
          case "block-quote":
            return `> ${text}`;
          case "bulleted-list-item":
            return `- ${text}`;
          case "numbered-list-item":
            return `1. ${text}`;
          case "code-block":
            return `\`\`\`\n${text}\n\`\`\``;
          case "math-block":
            return `\\[\n${node.latex || text}\n\\]`;
          default:
            return text;
        }
      })
      .join("\n");
  }, []);

  // Convert markdown to HTML for preview
  const renderMarkdown = useCallback((text) => {
    if (!text) return "";

    // First, extract and replace display math blocks to protect them from other regex
    const displayMathBlocks = [];
    let processed = text;

    // Multi-line \[ ... \]
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
      const idx = displayMathBlocks.length;
      displayMathBlocks.push(renderLatex(latex, true));
      return `%%DISPLAY_MATH_${idx}%%`;
    });

    // Multi-line $$ ... $$
    processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
      const idx = displayMathBlocks.length;
      displayMathBlocks.push(renderLatex(latex, true));
      return `%%DISPLAY_MATH_${idx}%%`;
    });

    // Inline math: \( ... \) and $ ... $ (single dollar, not double)
    processed = processed.replace(/\\\((.+?)\\\)/g, (_, latex) =>
      renderLatex(latex, false),
    );
    processed = processed.replace(
      /(?<!\$)\$(?!\$)(.+?)(?<!\$)\$(?!\$)/g,
      (_, latex) => renderLatex(latex, false),
    );

    let html = processed
      // Headers (must be done before other replacements)
      .replace(/^### (.*)$/gim, "<h3>$1</h3>")
      .replace(/^## (.*)$/gim, "<h2>$1</h2>")
      .replace(/^# (.*)$/gim, "<h1>$1</h1>")
      // Bold
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/__(.+?)__/g, "<strong>$1</strong>")
      // Italic (but not already in bold)
      .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
      .replace(/_([^_]+?)_/g, "<em>$1</em>")
      // Code
      .replace(/`([^`]+?)`/g, "<code>$1</code>")
      // Blockquote
      .replace(/^> (.*)$/gim, "<blockquote>$1</blockquote>")
      // Lists
      .replace(/^- (.*)$/gim, "<li>$1</li>")
      .replace(/^1\. (.*)$/gim, "<li>$1</li>")
      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      );

    // Wrap lines in paragraphs (only if not already a heading, list, or empty)
    html = html
      .split("\n")
      .map((line) => {
        const trimmed = line.trim();
        if (
          !trimmed ||
          trimmed.startsWith("<h") ||
          trimmed.startsWith("<li") ||
          trimmed.startsWith("<blockquote") ||
          trimmed.includes("%%DISPLAY_MATH_")
        ) {
          return line;
        }
        return `<p>${line}</p>`;
      })
      .join("\n");

    // Restore display math blocks
    displayMathBlocks.forEach((mathHtml, idx) => {
      html = html.replace(
        `%%DISPLAY_MATH_${idx}%%`,
        `<div class="${styles.mathBlock}">${mathHtml}</div>`,
      );
    });

    return html;
  }, []);

  // Strip markdown syntax from text for matching
  const stripMarkdown = (text) => {
    if (!text) return "";
    return text
      .replace(/^#{1,6}\s+/gm, "") // Remove header markers
      .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold
      .replace(/\*(.+?)\*/g, "$1") // Remove italic
      .replace(/__(.+?)__/g, "$1") // Remove bold alt
      .replace(/_(.+?)_/g, "$1") // Remove italic alt
      .replace(/`(.+?)`/g, "$1") // Remove inline code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove links, keep text
      .replace(/^[-*+]\s+/gm, "") // Remove list markers
      .replace(/^\d+\.\s+/gm, "") // Remove numbered list markers
      .replace(/^>\s+/gm, "") // Remove blockquote markers
      .replace(/\n+/g, " ") // Replace newlines with spaces
      .trim();
  };

  // Render preview with citation highlighting - wrap cited sentences in clickable spans
  const renderPreviewWithCitations = useCallback(
    (markdownText) => {
      if (!markdownText) return "";

      // First convert markdown to HTML
      let html = renderMarkdown(markdownText);

      // Then wrap sentences that have citations with clickable styling
      citations.forEach((citation) => {
        const prefix = citation.summary_sentence.substring(0, 40).toLowerCase();
        if (editedSentences.has(prefix)) return; // Skip edited sentences

        try {
          // Strip markdown from sentence and get just the text
          const strippedSentence = stripMarkdown(citation.summary_sentence);
          // Use full sentence for matching instead of truncating
          const textSnippet = strippedSentence;

          if (!textSnippet || textSnippet.length < 10) return; // Skip very short snippets

          // Escape ALL special regex characters
          const escapedSnippet = textSnippet.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
          );

          // Replace the sentence with a clickable span
          const regex = new RegExp(`(${escapedSnippet})`, "gi");
          html = html.replace(
            regex,
            `<span class="${styles.citationSentence}" data-citation-id="${citation.id}">$1</span>`,
          );
        } catch (e) {
          // Skip this citation if regex fails
          console.warn("Citation regex error:", e);
        }
      });

      return html;
    },
    [renderMarkdown, citations, editedSentences],
  );

  // Initialize editor value from markdown content
  const [value, setValue] = useState(() => markdownToSlate(content));
  const prevContentRef = useRef(content);

  // Update value when content changes externally (e.g., streaming)
  useEffect(() => {
    // Only update if content actually changed
    if (content !== prevContentRef.current) {
      prevContentRef.current = content;
      isInternalUpdate.current = true;

      const newValue = markdownToSlate(content);
      setValue(newValue);

      // Directly update editor children to ensure view refreshes
      editor.children = newValue;
      editor.onChange();

      // Reset the selection to prevent invalid path errors during streaming updates
      if (isStreaming) {
        Transforms.deselect(editor);
      }

      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 0);
    }
  }, [content, editor, markdownToSlate, isStreaming]);

  // Update stats
  useEffect(() => {
    const markdown = slateToMarkdown(value);
    setCharCount(markdown.length);
    setLineCount(value.length);
  }, [value, slateToMarkdown]);

  const handleChange = (newValue) => {
    // Track which sentences are being edited (to invalidate their citations)
    if (!isInternalUpdate.current && !isStreaming) {
      newValue.forEach((node) => {
        const text = node.children?.map((c) => c.text || "").join("") || "";
        if (text && text.length > 10) {
          const prefix = text.substring(0, 40).toLowerCase();
          // Check if this text differs from original citation sentences
          const matchingCitation = citations.find(
            (c) => c.summary_sentence.substring(0, 40).toLowerCase() === prefix,
          );
          const originalText = matchingCitation?.summary_sentence;
          if (originalText && text !== originalText.substring(0, text.length)) {
            setEditedSentences((prev) => new Set([...prev, prefix]));
          }
        }
      });
    }

    setValue(newValue);
    // Only call onUpdate if this is a user edit, not an external update
    if (!isInternalUpdate.current && !isStreaming) {
      const markdown = slateToMarkdown(newValue);
      onUpdate(markdown);
    }
  };

  // Toolbar actions
  const toggleMark = (format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const toggleBlock = (format) => {
    const isActive = isBlockActive(editor, format);
    Transforms.setNodes(
      editor,
      { type: isActive ? "paragraph" : format },
      { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) },
    );
  };

  const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const isBlockActive = (editor, format) => {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => SlateElement.isElement(n) && n.type === format,
      }),
    );

    return !!match;
  };

  // Rendering components
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  // Store selection for context menu
  const [selectedTextForMenu, setSelectedTextForMenu] = useState("");
  const [selectionRange, setSelectionRange] = useState(null);
  const [customInstruction, setCustomInstruction] = useState("");
  const customInstructionRef = useRef(null);

  // Context menu for text transformation
  const handleContextMenu = (e) => {
    e.preventDefault();

    try {
      if (!editor || !editor.selection) {
        return;
      }

      const { selection } = editor;
      const selectedText = Editor.string(editor, selection);

      if (!selectedText || selectedText.trim() === "") {
        return;
      }

      setSelectedTextForMenu(selectedText);
      setSelectionRange(selection);
      setCustomInstruction("");

      // Position the floating toolbar near the selection
      const domSelection = window.getSelection();
      if (domSelection && domSelection.rangeCount > 0) {
        const range = domSelection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const containerEl = containerRef.current;
        if (!containerEl) return;
        const containerRect = containerEl.getBoundingClientRect();

        // Center horizontally on the selection, position above it
        const x = rect.left - containerRect.left + rect.width / 2;
        const y = rect.top - containerRect.top - 8;

        setContextMenu({ visible: true, x, y });

        // Focus the custom instruction input after render
        setTimeout(() => {
          customInstructionRef.current?.focus();
        }, 50);
      }
    } catch (error) {
      console.error("Error in handleContextMenu:", error);
    }
  };

  const applyOperation = async (operation) => {
    if (!selectedTextForMenu) return;

    // Save position for loading indicator before hiding toolbar
    setProcessingPos({ x: contextMenu.x, y: contextMenu.y });
    setContextMenu({ visible: false, x: 0, y: 0 });
    setIsProcessing(true);

    try {
      const processed = await postProcessText(operation, selectedTextForMenu);
      setConfirmDialog({
        visible: true,
        original: selectedTextForMenu,
        processed: processed,
        operation: operation,
      });
    } catch (error) {
      console.error("Error processing text:", error);
    } finally {
      setIsProcessing(false);
      setProcessingPos(null);
    }
  };

  const applyCustomInstruction = async () => {
    if (!selectedTextForMenu || !customInstruction.trim()) return;

    // Save position for loading indicator before hiding toolbar
    setProcessingPos({ x: contextMenu.x, y: contextMenu.y });
    setContextMenu({ visible: false, x: 0, y: 0 });
    setIsProcessing(true);

    try {
      const processed = await editSelection(
        selectedTextForMenu,
        customInstruction.trim(),
      );
      setConfirmDialog({
        visible: true,
        original: selectedTextForMenu,
        processed: processed,
        operation: customInstruction.trim(),
      });
    } catch (error) {
      console.error("Error processing custom instruction:", error);
    } finally {
      setIsProcessing(false);
      setProcessingPos(null);
      setCustomInstruction("");
    }
  };

  const acceptReplacement = () => {
    if (selectionRange && confirmDialog.processed) {
      Transforms.delete(editor, { at: selectionRange });
      Transforms.insertText(editor, confirmDialog.processed, {
        at: selectionRange.anchor,
      });
    }
    setConfirmDialog({
      visible: false,
      original: "",
      processed: "",
      operation: "",
    });
    setSelectedTextForMenu("");
    setSelectionRange(null);
  };

  const rejectReplacement = () => {
    setConfirmDialog({
      visible: false,
      original: "",
      processed: "",
      operation: "",
    });
    setSelectedTextForMenu("");
    setSelectionRange(null);
  };

  // Close context menu
  useEffect(() => {
    const handleClickOutside = () =>
      setContextMenu({ visible: false, x: 0, y: 0 });
    if (contextMenu.visible) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [contextMenu.visible]);

  return (
    <div className={styles.richTextEditorContainer} ref={containerRef}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarSection}>
          <button
            className={`${styles.editToggleBtn} ${!showPreview ? styles.editToggleBtnActive : ""}`}
            onClick={() => setShowPreview(!showPreview)}
            title={showPreview ? "Edit Mode" : "Preview Mode"}
          >
            <MdEdit /> {showPreview ? "Edit" : "Editing"}
          </button>
        </div>

        {!showPreview && (
          <>
            <div className={styles.toolbarDivider}></div>

            <div className={styles.toolbarSection}>
              <button
                className={`${styles.toolbarBtn} ${isMarkActive(editor, "bold") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("bold");
                }}
                title="Bold (Ctrl+B)"
              >
                <MdFormatBold />
              </button>
              <button
                className={`${styles.toolbarBtn} ${isMarkActive(editor, "italic") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("italic");
                }}
                title="Italic (Ctrl+I)"
              >
                <MdFormatItalic />
              </button>
              <button
                className={`${styles.toolbarBtn} ${isMarkActive(editor, "underline") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("underline");
                }}
                title="Underline (Ctrl+U)"
              >
                <MdFormatUnderlined />
              </button>
              <button
                className={`${styles.toolbarBtn} ${isMarkActive(editor, "code") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("code");
                }}
                title="Code"
              >
                <MdCode />
              </button>
            </div>

            <div className={styles.toolbarDivider}></div>

            <div className={styles.toolbarSection}>
              <button
                className={`${styles.toolbarBtn} ${isBlockActive(editor, "heading-one") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleBlock("heading-one");
                }}
                title="Heading 1"
              >
                H1
              </button>
              <button
                className={`${styles.toolbarBtn} ${isBlockActive(editor, "heading-two") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleBlock("heading-two");
                }}
                title="Heading 2"
              >
                H2
              </button>
              <button
                className={`${styles.toolbarBtn} ${isBlockActive(editor, "block-quote") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleBlock("block-quote");
                }}
                title="Quote"
              >
                <MdFormatQuote />
              </button>
            </div>

            <div className={styles.toolbarDivider}></div>

            <div className={styles.toolbarSection}>
              <button
                className={`${styles.toolbarBtn} ${isBlockActive(editor, "bulleted-list-item") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleBlock("bulleted-list-item");
                }}
                title="Bullet List"
              >
                <MdFormatListBulleted />
              </button>
              <button
                className={`${styles.toolbarBtn} ${isBlockActive(editor, "numbered-list-item") ? styles.active : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleBlock("numbered-list-item");
                }}
                title="Numbered List"
              >
                <MdFormatListNumbered />
              </button>
            </div>
          </>
        )}

        <div className={styles.toolbarSpacer} />

        <div className={styles.toolbarSection}>
          <button
            className={styles.saveBtn}
            onClick={handleSaveClick}
            disabled={isBusy}
            title="Save this summary to history"
          >
            {isSaved ? (
              <>
                <MdCheckCircle /> Saved
              </>
            ) : (
              <>
                <MdPushPin /> Save
              </>
            )}
          </button>
          <ExportButton
            onClick={() => setShowExportModal(true)}
            disabled={!hasSummary || isBusy}
            buttonText="Export"
          />
          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            exportData={exportData}
          />
        </div>
      </div>

      <div className={styles.editorArea}>
        {showPreview ? (
          <div
            className={styles.previewPane}
            dangerouslySetInnerHTML={{
              __html: renderPreviewWithCitations(slateToMarkdown(value)),
            }}
            onClick={(e) => {
              // Handle clicks on citation sentences - check if target has the class or is inside a span with it
              let citationSpan = e.target;
              if (!citationSpan.classList?.contains(styles.citationSentence)) {
                citationSpan = e.target.closest(`.${styles.citationSentence}`);
              }

              if (citationSpan && onCitationClick) {
                const citationId = parseInt(
                  citationSpan.dataset.citationId,
                  10,
                );
                if (citationId) {
                  onCitationClick(citationId);
                }
              }
            }}
          />
        ) : (
          <div onContextMenu={handleContextMenu}>
            <Slate
              editor={editor}
              initialValue={value}
              value={value}
              onChange={handleChange}
            >
              <Editable
                className={styles.editableArea}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder="Your summary will appear here..."
                readOnly={isStreaming || isProcessing}
              />
            </Slate>
          </div>
        )}
      </div>

      {/* Floating Transform Toolbar */}
      {contextMenu.visible && (
        <div
          className={styles.transformToolbar}
          style={{
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className={styles.transformActions}>
            <button
              className={styles.transformBtn}
              onClick={() => applyOperation("Shorten")}
              title="Shorten"
            >
              <MdContentCut />
            </button>
            <button
              className={styles.transformBtn}
              onClick={() => applyOperation("Simplify")}
              title="Simplify"
            >
              <MdLightbulb />
            </button>
            <button
              className={styles.transformBtn}
              onClick={() => applyOperation("Rephrase")}
              title="Rephrase"
            >
              <MdRefresh />
            </button>
            <button
              className={styles.transformBtn}
              onClick={() => applyOperation("Expand")}
              title="Expand"
            >
              <MdExpandMore />
            </button>
            <div className={styles.transformDivider} />
            <div className={styles.customInstructionWrapper}>
              <input
                ref={customInstructionRef}
                type="text"
                className={styles.customInstructionInput}
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customInstruction.trim()) {
                    applyCustomInstruction();
                  }
                  if (e.key === "Escape") {
                    setContextMenu({ visible: false, x: 0, y: 0 });
                  }
                }}
                placeholder="Custom instruction..."
              />
              <button
                className={`${styles.transformBtn} ${styles.sendBtn}`}
                onClick={applyCustomInstruction}
                disabled={!customInstruction.trim()}
                title="Apply custom instruction"
              >
                <MdSend />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline Loading Indicator */}
      {isProcessing && processingPos && (
        <div
          className={styles.processingIndicator}
          style={{
            top: `${processingPos.y}px`,
            left: `${processingPos.x}px`,
          }}
        >
          <div className={styles.processingSpinner} />
          <span>Processing...</span>
        </div>
      )}

      {/* Status Bar */}
      <div className={styles.statusBar}>
        <span className={styles.statusItem}>Lines: {lineCount}</span>
        <span className={styles.statusItem}>Characters: {charCount}</span>
        {isStreaming && (
          <span className={styles.statusItem}>
            <MdBolt /> Generating...
          </span>
        )}
        {isProcessing && (
          <span className={styles.statusItem}>
            <MdHourglassEmpty /> Processing...
          </span>
        )}
      </div>

      {/* Confirmation Dialog - Inline */}
      {confirmDialog.visible && (
        <div className={styles.confirmDialog}>
          <div className={styles.dialogHeader}>
            <span className={styles.dialogTitle}>
              <MdCheck className={styles.dialogTitleIcon} /> Replace &mdash;{" "}
              <em>{confirmDialog.operation}</em>
            </span>
            <button className={styles.closeBtn} onClick={rejectReplacement}>
              <MdClose />
            </button>
          </div>
          <div className={styles.dialogContent}>
            <div className={styles.comparisonSection}>
              <label>Original</label>
              <div className={styles.originalBox}>{confirmDialog.original}</div>
            </div>
            <div className={styles.comparisonSection}>
              <label>Replacement</label>
              <div className={styles.processedBox}>
                {confirmDialog.processed}
              </div>
            </div>
          </div>
          <div className={styles.dialogActions}>
            <button className={styles.rejectBtn} onClick={rejectReplacement}>
              <MdClose /> Reject
            </button>
            <button className={styles.acceptBtn} onClick={acceptReplacement}>
              <MdCheck /> Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Element renderer - plain rendering in edit mode (citations shown in preview only)
const Element = ({ attributes, children, element }) => {
  switch (element.type) {
    case "heading-one":
      return <h1 {...attributes}>{children}</h1>;
    case "heading-two":
      return <h2 {...attributes}>{children}</h2>;
    case "heading-three":
      return <h3 {...attributes}>{children}</h3>;
    case "block-quote":
      return <blockquote {...attributes}>{children}</blockquote>;
    case "bulleted-list-item":
      return <li {...attributes}>{children}</li>;
    case "numbered-list-item":
      return <li {...attributes}>{children}</li>;
    case "code-block":
      return (
        <pre {...attributes}>
          <code>{children}</code>
        </pre>
      );
    case "math-block":
      return (
        <div
          {...attributes}
          contentEditable={false}
          className={styles.mathBlock}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: renderLatex(element.latex || "", true),
            }}
          />
          <span style={{ display: "none" }}>{children}</span>
        </div>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// Leaf renderer
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  return <span {...attributes}>{children}</span>;
};

export default RichTextEditor;
